import { API_CONFIG } from '../config/api';

const processEvent = (eventLines) => {
  const eventTypeLine = eventLines.find((l) => l.startsWith("event: "));
  const dataLine = eventLines.find((l) => l.startsWith("data: "));

  if (!eventTypeLine || !dataLine) return { deltaText: "", isDone: false };

  const eventType = eventTypeLine.replace("event: ", "");
  const dataStr = dataLine.replace("data: ", "");

  if (eventType === "content_block_delta") {
    try {
      const data = JSON.parse(dataStr);
      const deltaText = data?.delta?.text || "";
      return { deltaText, isDone: false };
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return { deltaText: "", isDone: false };
    }
  } else if (eventType === "done") {
    return { deltaText: "", isDone: true };
  }

  return { deltaText: "", isDone: false };
};

const deduplicateText = (text) => {
  const tokens = text.split(/(\s+)/);
  const cleanedTokens = [];
  let lastWord = null;
  for (const token of tokens) {
    if (/\s+/.test(token)) {
      cleanedTokens.push(token);
    } else {
      if (lastWord !== token) {
        cleanedTokens.push(token);
        lastWord = token;
      }
    }
  }
  return cleanedTokens.join("");
};

export const useChatApi = () => {
  const uploadFile = async (file) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPLOAD}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const sendMessage = async (input, fileUuids = [], setMessages, setLoading, onStreamComplete) => {
    if (!input.trim() && fileUuids.length === 0) return;
    
    setLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CONVERSATION}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: input || "",
          files: fileUuids
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body from server");
      }

      // Add empty bot message for streaming
      setMessages((prev) => [...prev, { sender: "bot", text: "" }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = "";
      let eventLines = [];
      let botTextBuffer = "";
      let pendingText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value);
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line === "") {
            const { deltaText, isDone } = processEvent(eventLines);
            eventLines = [];

            if (deltaText) {
              pendingText += deltaText;
              let spaceIndex;
              while ((spaceIndex = pendingText.indexOf(" ")) !== -1) {
                const word = pendingText.slice(0, spaceIndex + 1);
                pendingText = pendingText.slice(spaceIndex + 1);

                botTextBuffer += word;
                const deduplicatedBuffer = deduplicateText(botTextBuffer);

                setMessages((prev) => {
                  const updated = [...prev];
                  const lastBotIndex = updated.findLastIndex((m) => m.sender === "bot");
                  if (lastBotIndex !== -1) {
                    updated[lastBotIndex].text = deduplicatedBuffer;
                  }
                  return updated;
                });
              }
            }

            if (isDone) {
              if (pendingText) {
                botTextBuffer += pendingText;
                const deduplicatedBuffer = deduplicateText(botTextBuffer);
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastBotIndex = updated.findLastIndex((m) => m.sender === "bot");
                  if (lastBotIndex !== -1) {
                    updated[lastBotIndex].text = deduplicatedBuffer;
                  }
                  return updated;
                });
              }
              break;
            }
          } else {
            eventLines.push(line);
          }
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
      if (onStreamComplete) {
        onStreamComplete();
      }
    }
  };

  return { uploadFile, sendMessage };
};